import { useState } from 'react';

import useEscapeKey from '../hooks/useEscapeKey';
import { useTranslation } from '../i18n';
import { getImageUrl } from '../utils/imageUrl';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Flag,
  MessageCircle,
  FileText,
  Settings,
  UserCog,
  LogOut,
  Menu,
  X,
  Rss,
} from 'lucide-react';

import LanguageSwitcher from './LanguageSwitcher';

interface ILayoutProps {
  user: {
    username: string;
    displayName?: string;
    email?: string;
    avatar?: string;
    isOwner?: boolean;
    permissions?: string[];
  };
  onLogout: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  children: React.ReactNode;
}

const NAV_KEYS = [
  {
    key: 'dashboard',
    labelKey: 'nav.dashboard',
    icon: LayoutDashboard,
    permission: null,
  },
  {
    key: 'posts',
    labelKey: 'nav.x_posts',
    icon: Rss,
    permission: 'x_posts',
  },
  { key: 'users', labelKey: 'nav.users', icon: Users, permission: 'users' },
  {
    key: 'spaces',
    labelKey: 'nav.spaces',
    icon: MessageSquare,
    permission: 'spaces',
  },
  {
    key: 'reports',
    labelKey: 'nav.reports',
    icon: Flag,
    permission: 'reports',
  },
  {
    key: 'feedback',
    labelKey: 'nav.feedback',
    icon: MessageCircle,
    permission: 'feedback',
  },
  {
    key: 'admin-management',
    labelKey: 'nav.admin_management',
    icon: UserCog,
    permission: 'admins',
  },
  {
    key: 'audit-logs',
    labelKey: 'nav.audit_logs',
    icon: FileText,
    permission: 'audit_logs',
  },
  {
    key: 'platform-config',
    labelKey: 'nav.platform_config',
    icon: Settings,
    permission: 'settings',
  },
];

const Layout = ({
  user,
  onLogout,
  activeTab,
  onTabChange,
  children,
}: ILayoutProps) => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // The mobile sidebar dismisses on backdrop click; Escape is the keyboard
  // equivalent.
  useEscapeKey(() => setSidebarOpen(false), sidebarOpen);

  const handleNavClick = (key: string) => {
    onTabChange(key);
    setSidebarOpen(false);
  };

  const navItems = NAV_KEYS.filter(
    ({ permission }) =>
      !permission || user.isOwner || user.permissions?.includes(permission),
  );

  const sidebarContent = (
    <>
      {/* Logo / Title */}
      <div className="h-16 flex items-center justify-between pl-6 pr-4 border-b border-[#E5E7EB]">
        <h1 className="text-xl font-bold tracking-tight text-[#101828]">
          {t('app.title')}
        </h1>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 flex flex-col gap-1 px-4 pt-4 overflow-y-auto">
        {navItems.map(({ key, labelKey, icon: Icon }) => (
          <button
            key={key}
            onClick={() => handleNavClick(key)}
            className={`w-full flex items-center gap-3 pl-4 py-2 rounded-[10px] text-base font-medium transition-colors ${
              activeTab === key
                ? 'bg-[#EFF6FF] text-[#155DFC]'
                : 'text-[#364153] hover:bg-gray-50'
            }`}
          >
            <Icon className="w-5 h-5 shrink-0" />
            {t(labelKey)}
          </button>
        ))}
      </nav>

      {/* User section */}
      <div className="border-t border-[#E5E7EB] px-4 py-4">
        <div className="flex items-center gap-3 px-4">
          <button
            onClick={() => handleNavClick('my-account')}
            className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-base font-medium shrink-0 overflow-hidden">
              {user.avatar ? (
                <img
                  src={getImageUrl(user.avatar)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                (user.username || t('layout.admin')).charAt(0)
              )}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-sm font-medium text-[#101828] truncate leading-5">
                {user.displayName || t('layout.admin')}
              </div>
              <div className="text-xs text-[#6A7282] truncate leading-4">
                {user.email || user.username}
              </div>
            </div>
          </button>
          <button
            onClick={onLogout}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title={t('layout.logout')}
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#F9FAFB]">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-[#E5E7EB] flex items-center px-4 z-40">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 text-gray-600 hover:text-gray-900"
        >
          <Menu className="w-6 h-6" />
        </button>
        <h1 className="ml-3 text-lg font-bold text-[#101828]">
          {t('app.title')}
        </h1>
      </div>

      {/* Mobile overlay — a real button, so the drawer can be dismissed by
          keyboard as well as by tapping outside it. A div with onClick is not
          focusable and offers keyboard users nothing (typescript:S1082). */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label={t('common.close')}
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - desktop: always visible, mobile: slide-in drawer */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r border-[#E5E7EB] flex flex-col shrink-0
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {sidebarContent}
      </aside>

      {/* Main content area */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">{children}</main>
    </div>
  );
};

export default Layout;
